import { ref } from 'vue';

const DEFAULT_THRESHOLD = 80;
const MAX_PULL_DISTANCE = 120;
const SCROLL_TOP_EPSILON = 2;

/**
 * Pull-to-refresh gesture for Vue 3 + Capacitor (plain WebView, no Ionic).
 *
 * - Arms only when scroll is at top of scrollRoot.
 * - Adds touchmove with { passive: false } only after a qualifying touchstart
 *   so normal scrolling stays passive until a pull-from-top begins.
 *
 * @param {Object} options
 * @param {() => void | Promise<void>} options.onRefresh - Called when pull exceeds threshold (async OK).
 * @param {Window | HTMLElement | null} [options.scrollRoot=window] - Scroll container; window uses scrollY/documentElement.
 * @param {number} [options.threshold=80] - Minimum pull distance to trigger refresh.
 */
export function usePullToRefresh(options = {}) {
  const {
    onRefresh = async () => {},
    scrollRoot = typeof window !== 'undefined' ? window : null,
    threshold = DEFAULT_THRESHOLD
  } = options;

  const isPulling = ref(false);
  const pullDistance = ref(0);
  const isRefreshing = ref(false);

  let startY = 0;
  let moveListenerAttached = false;

  function getScrollTop() {
    if (scrollRoot == null) return 0;
    if (scrollRoot === window) {
      return window.scrollY ?? document.documentElement.scrollTop ?? 0;
    }
    return scrollRoot.scrollTop ?? 0;
  }

  function getListenerTarget() {
    if (scrollRoot == null || scrollRoot === window) {
      return typeof window !== 'undefined' ? window : null;
    }
    return scrollRoot;
  }

  function removeTouchMovePull() {
    if (!moveListenerAttached || typeof window === 'undefined') return;
    window.removeEventListener('touchmove', onTouchMovePull, true);
    moveListenerAttached = false;
  }

  function onTouchMovePull(e) {
    if (isRefreshing.value) {
      removeTouchMovePull();
      return;
    }

    if (getScrollTop() > SCROLL_TOP_EPSILON) {
      removeTouchMovePull();
      isPulling.value = false;
      pullDistance.value = 0;
      return;
    }

    const touch = e.touches[0];
    if (!touch) return;

    const delta = touch.clientY - startY;
    if (delta <= 0) {
      isPulling.value = false;
      pullDistance.value = 0;
      return;
    }

    isPulling.value = true;
    pullDistance.value = Math.min(delta, MAX_PULL_DISTANCE);
    e.preventDefault();
  }

  function onTouchStart(e) {
    if (typeof window === 'undefined' || isRefreshing.value) return;
    if (getScrollTop() > SCROLL_TOP_EPSILON) return;

    const touch = e.touches[0];
    if (!touch) return;

    startY = touch.clientY;
    isPulling.value = false;
    pullDistance.value = 0;

    if (!moveListenerAttached) {
      window.addEventListener('touchmove', onTouchMovePull, { passive: false, capture: true });
      moveListenerAttached = true;
    }
  }

  async function onTouchEnd() {
    removeTouchMovePull();

    const distance = pullDistance.value;
    const wasPulling = isPulling.value;

    isPulling.value = false;
    pullDistance.value = 0;

    if (!wasPulling || distance < threshold || isRefreshing.value) {
      return;
    }

    isRefreshing.value = true;
    try {
      await Promise.resolve(onRefresh());
    } catch (err) {
      console.error('[usePullToRefresh] onRefresh failed:', err);
    } finally {
      isRefreshing.value = false;
    }
  }

  function onTouchCancel() {
    removeTouchMovePull();
    isPulling.value = false;
    pullDistance.value = 0;
  }

  function attachPullListeners() {
    const target = getListenerTarget();
    if (!target || typeof target.addEventListener !== 'function') return;

    target.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
    target.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
    target.addEventListener('touchcancel', onTouchCancel, { passive: true, capture: true });
  }

  function detachPullListeners() {
    const target = getListenerTarget();
    if (target && typeof target.removeEventListener === 'function') {
      target.removeEventListener('touchstart', onTouchStart, true);
      target.removeEventListener('touchend', onTouchEnd, true);
      target.removeEventListener('touchcancel', onTouchCancel, true);
    }
    removeTouchMovePull();
    isPulling.value = false;
    pullDistance.value = 0;
  }

  return {
    isPulling,
    pullDistance,
    isRefreshing,
    attachPullListeners,
    detachPullListeners
  };
}
